import { ART_PAL, ball, body, fade, mix, n, rng, shade, tint, vgrad, type ArtTheme } from '../covers/kit';
import { defineScene, type SceneVariant } from './types';

/**
 * Pirate Cove scene behind the game zone: a friendly cove — an evening bay with a moon, stars and warm lanterns on a
 * wooden pier in dark, a sunny turquoise bay with sand, a palm and gulls in light. Left: the pier with two lanterns and a
 * far sail; right: the moon / sun, a lighthouse on a rock and a palm on a sandbank. The centre stays calm (the machine
 * sits there): only sky, water and a soft pool of light. Motifs are marked `data-motif="<x>"` (tests). No filters,
 * no text: a static image, cheap to paint.
 */
export function pirateCoveScene(theme: ArtTheme, v: SceneVariant): string {
  const P = ART_PAL[theme];
  const p = `sp${theme[0]}${v[0]}`;
  const wide = v === 'wide';
  const W = wide ? 1600 : 400;
  const H = wide ? 900 : 800;
  const hz = wide ? 470 : 330;

  const sky = P.dark ? mix(P.bg, P.blue, 0.24) : mix(P.sky, '#FFFFFF', 0.62);
  const skyLow = P.dark ? mix(P.bg, P.purple, 0.16) : mix(P.sky, '#FFFFFF', 0.86);
  const sea = P.dark ? mix(P.bg, P.teal, 0.2) : mix(P.teal, '#FFFFFF', 0.55);
  const seaDeep = P.dark ? mix(P.bg, P.teal, 0.1) : mix(P.teal, P.sky, 0.3);
  const sand = P.dark ? mix(P.bg, P.gold, 0.18) : mix(P.gold, '#FFFFFF', 0.62);
  const wood = shade(mix(P.orange, P.gold, 0.3), P.dark ? 0.6 : 0.35);
  const rock = P.dark ? mix(P.bg, P.teal, 0.28) : mix(P.teal, P.green, 0.45);
  const cream = P.dark ? mix('#F3E9D2', P.bg, 0.35) : '#FFF8E8';
  const lamp = P.dark ? '#FFE9A8' : '#FFE7A3';
  const palm = P.dark ? shade(P.green, 0.55) : mix(P.green, P.teal, 0.25);
  const ink = P.dark ? '#FFFFFF' : shade(P.teal, 0.45);
  const o = (dark: number, light: number) => n(P.dark ? dark : light);

  /** a pier lantern on a post: glow + a warm glass box with caps */
  const lantern = (x: number, y: number, s: number) =>
    `<g data-motif="${Math.round(x)}"><circle cx="${n(x)}" cy="${n(y)}" r="${n(s * 5)}" fill="url(#${p}lh)"/>` +
    `<rect x="${n(x - s * 0.7)}" y="${n(y - s)}" width="${n(s * 1.4)}" height="${n(s * 2)}" rx="${n(s * 0.3)}" fill="${lamp}" fill-opacity="${o(0.95, 0.85)}"/>` +
    `<path d="M${n(x - s)} ${n(y - s)}h${n(s * 2)}M${n(x - s)} ${n(y + s)}h${n(s * 2)}" stroke="${wood}" stroke-width="${n(s * 0.4)}"/></g>`;
  /** a palm: a curved trunk and six fronds */
  const palmTree = (x: number, y: number, s: number, flip = 1) => {
    const fr = [-150, -110, -70, -30, 10, 40]
      .map(
        (a) =>
          `<path d="M0 0C10 -14 34 -16 52 -4C34 -8 16 -4 0 0Z" transform="rotate(${a})" fill="url(#${p}pm)"/>`,
      )
      .join('');
    return (
      `<g data-motif="${Math.round(x)}" transform="translate(${x} ${y}) scale(${n(s * flip)} ${n(s)})">` +
      `<path d="M0 0Q-6 -60 18 -120" fill="none" stroke="${wood}" stroke-width="10" stroke-linecap="round"/>` +
      `<path d="M-4 -30h9M-3 -60h9M3 -90h9" stroke="${shade(wood, 0.3)}" stroke-width="2"/>` +
      `<g transform="translate(18 -120)">${fr}</g></g>`
    );
  };
  /** a far lighthouse on a rock, a soft beam */
  const lighthouse = (x: number, y: number, s: number) =>
    `<g data-motif="${Math.round(x)}" transform="translate(${x} ${y}) scale(${s})">` +
    `<path d="M-60 0Q-40 -26 0 -28Q40 -26 60 0Z" fill="${rock}"/>` +
    `<path d="M10 -78L90 -96V-60Z" fill="${lamp}" fill-opacity="${o(0.3, 0.45)}"/>` +
    `<path d="M-10 -26L-7 -74H7L10 -26Z" fill="${cream}"/>` +
    `<path d="M-9 -44H9M-8 -60H8" stroke="${P.red}" stroke-opacity="${o(0.7, 0.9)}" stroke-width="7"/>` +
    `<rect x="-6" y="-84" width="12" height="10" fill="${lamp}"/><path d="M-9 -84L0 -94L9 -84Z" fill="${P.red}" fill-opacity="${o(0.7, 0.9)}"/></g>`;
  /** a far sail on the horizon */
  const sail = (x: number, y: number, s: number) =>
    `<g data-motif="${Math.round(x)}" transform="translate(${x} ${y}) scale(${s})" fill="${cream}" fill-opacity="${o(0.7, 0.9)}">` +
    `<path d="M0 -4L2 -50L22 -8Z"/><path d="M-4 -6L4 -54L-18 -10Z" fill-opacity=".8"/><path d="M-20 -2H26L20 6H-14Z" fill="${wood}"/></g>`;
  /** gulls (light) */
  const gulls = (pts: number[][]) =>
    pts
      .map(
        ([x, y, s]) =>
          `<path data-motif="${x}" d="M${x} ${y}q${n(7 * s!)} ${n(-7 * s!)} ${n(14 * s!)} 0q${n(7 * s!)} ${n(-7 * s!)} ${n(14 * s!)} 0" fill="none" stroke="${ink}" stroke-opacity=".5" stroke-width="2.2" stroke-linecap="round"/>`,
      )
      .join('');
  /** stars (dark), only in the side bands */
  const stars = (count: number, bands: number[][]) => {
    const r = rng(7);
    let s = '';
    for (let i = 0; i < count; i++) {
      const [a, b] = bands[i % bands.length]!;
      s += `<circle cx="${n(a! + r() * (b! - a!))}" cy="${n(20 + r() * (hz - 140))}" r="${n(0.8 + r() * 1.4)}" fill="#FFFFFF" fill-opacity="${n(0.3 + r() * 0.5)}"/>`;
    }
    return s;
  };
  /** short wave strokes */
  const waves = (pts: number[][]) =>
    `<path d="${pts.map(([x, y]) => `M${x} ${y}q10 -6 20 0t20 0`).join('')}" fill="none" stroke="#FFFFFF" stroke-opacity="${o(0.14, 0.55)}" stroke-width="2.4" stroke-linecap="round"/>`;

  const defs =
    `<defs>${vgrad(`${p}sk`, sky, skyLow, skyLow)}` +
    vgrad(`${p}se`, tint(sea, 0.08), sea, seaDeep) +
    fade(`${p}lh`, P.dark ? lamp : P.orange, P.dark ? 0.55 : 0.25) +
    fade(`${p}mn`, P.dark ? '#FFF1C2' : '#FFE7A3', P.dark ? 0.35 : 0.7) +
    ball(`${p}sun`, P.dark ? '#FFF1C2' : '#FFD166', 0.4, 0.1) +
    body(`${p}pm`, palm, 0.3, 0.3, 0.6, 1) +
    fade(`${p}calm`, P.dark ? mix(P.bg, P.teal, 0.25) : '#FFFFFF', P.dark ? 0.3 : 0.5) +
    `</defs>`;

  let art =
    `<rect width="${W}" height="${H}" fill="url(#${p}sk)"/>` +
    `<rect y="${hz}" width="${W}" height="${H - hz}" fill="url(#${p}se)"/>` +
    // the calm centre: a soft pool of light behind the machine
    `<ellipse cx="${W / 2}" cy="${H * 0.5}" rx="${n(W * 0.34)}" ry="${n(H * 0.42)}" fill="url(#${p}calm)"/>`;

  /** moon (dark, with its glitter on the water) or sun (light) at (x, y) */
  const orb = (x: number, y: number, r: number) =>
    `<g data-motif="${x}"><circle cx="${x}" cy="${y}" r="${r * 4}" fill="url(#${p}mn)"/>` +
    (P.dark
      ? `<path d="M${n(x + r * 0.31)} ${n(y - r * 0.95)}A${r} ${r} 0 1 0 ${n(x + r * 0.95)} ${n(y + r * 0.31)}A${n(r * 0.8)} ${n(r * 0.8)} 0 1 1 ${n(x + r * 0.31)} ${n(y - r * 0.95)}Z" fill="url(#${p}sun)"/>`
      : `<circle cx="${x}" cy="${y}" r="${r}" fill="url(#${p}sun)"/>`) +
    `<path d="M${x - r} ${hz + 20}h${r * 2}M${n(x - r * 0.7)} ${hz + 44}h${n(r * 1.4)}M${n(x - r * 0.4)} ${hz + 70}h${n(r * 0.8)}" stroke="${P.dark ? '#FFF1C2' : '#FFFFFF'}" stroke-opacity="${o(0.3, 0.7)}" stroke-width="4" stroke-linecap="round"/></g>`;

  if (wide) {
    const pier =
      `<g data-motif="190"><path d="M0 610H400V630H0Z" fill="${wood}"/>` +
      `<path d="M20 610V720M140 610V740M260 610V730M380 610V712" stroke="${shade(wood, 0.25)}" stroke-width="16"/>` +
      `<path d="M0 610H400" stroke="${tint(wood, 0.3)}" stroke-opacity=".6" stroke-width="3"/>` +
      `<path d="M130 610V530M330 610V546" stroke="${wood}" stroke-width="8"/>` +
      `<path d="M130 548Q230 600 330 560" fill="none" stroke="${tint(wood, 0.4)}" stroke-opacity=".7" stroke-width="3"/></g>`;
    art +=
      (P.dark
        ? stars(26, [
            [30, 440],
            [1160, 1580],
          ])
        : gulls([
            [200, 150, 1.2],
            [250, 128, 0.9],
            [1180, 120, 1],
            [1220, 170, 0.8],
          ])) +
      orb(1390, 150, 48) +
      sail(330, hz + 4, 1.2) +
      lighthouse(1250, hz + 6, 1.1) +
      waves([
        [60, 520],
        [300, 560],
        [40, 800],
        [1200, 560],
        [1420, 520],
        [1300, 820],
      ]) +
      pier +
      lantern(130, 520, 12) +
      lantern(330, 536, 10) +
      // sandbank with a palm, bottom right; a bit of beach bottom left
      `<path data-motif="1450" d="M1180 900Q1300 760 1600 740V900Z" fill="${sand}"/>` +
      `<path data-motif="120" d="M0 820Q160 800 300 900H0Z" fill="${sand}"/>` +
      palmTree(1480, 790, 1.3, -1) +
      palmTree(1380, 820, 0.9);
  } else {
    art +=
      (P.dark
        ? stars(10, [[10, 390]])
        : gulls([
            [40, 90, 0.9],
            [210, 56, 0.8],
          ])) +
      orb(320, 110, 30) +
      sail(60, hz + 4, 0.8) +
      waves([
        [30, 400],
        [320, 430],
        [40, 700],
      ]) +
      `<path data-motif="330" d="M200 800Q280 700 400 690V800Z" fill="${sand}"/>` +
      palmTree(360, 760, 0.8, -1) +
      `<path data-motif="40" d="M40 728V800" stroke="${wood}" stroke-width="6"/>` +
      lantern(40, 720, 9);
  }

  return (
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${W} ${H}" preserveAspectRatio="xMidYMid slice">` +
    defs +
    art +
    `</svg>`
  );
}

/** Header accent of Pirate Cove: a twisted sandy rope with a small brass ring (repeat-x tile, 64 × 12). */
export function pirateCoveAccent(theme: ArtTheme): string {
  const P = ART_PAL[theme];
  const rope = P.dark ? '#E6CB94' : '#C99A4E';
  const brass = P.dark ? P.gold : mix(P.gold, '#FFD166', 0.4);
  let twist = '';
  for (let x = 2; x < 64; x += 5) twist += `M${x} 3.4l3 5.2`;
  return (
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 12" width="64" height="12">` +
    `<path d="M0 6H64" stroke="${rope}" stroke-opacity=".7" stroke-width="4"/>` +
    `<path d="${twist}" stroke="${shade(rope, 0.4)}" stroke-opacity=".6" stroke-width="1.2"/>` +
    `<circle cx="32" cy="6" r="3.6" fill="none" stroke="${brass}" stroke-width="1.6"/>` +
    `</svg>`
  );
}

export default defineScene({ game: 'pirate-cove', scene: pirateCoveScene, accent: pirateCoveAccent });
